'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { useUser } from '@/firebase/auth/use-user';
import { doc, onSnapshot } from 'firebase/firestore';
import { useFirebase } from '@/firebase/client-provider';
import { License, Feature, LicenseType } from '@/lib/types';
import { LICENSE_FEATURES, LICENSE_LIMITS } from '@/lib/license-config';

interface LicenseContextValue {
  license: License | null;
  loading: boolean;
  hasFeature: (feature: Feature) => boolean;
  canAddProject: (currentOwnedCount: number) => boolean; // Only counts owned projects, not shared
  canAddTask: (currentCount: number) => boolean;
  canAddContributor: (currentCount: number) => boolean;
  getLimit: (limitType: keyof typeof LICENSE_LIMITS[LicenseType]) => number | null;
  isFeatureUpgradable: (feature: Feature) => boolean;
}

const LicenseContext = createContext<LicenseContextValue | undefined>(undefined);

export function LicenseProvider({ children }: { children: React.ReactNode }) {
  const { user } = useUser();
  const { firestore } = useFirebase();
  const [license, setLicense] = useState<License | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLicense(null);
      setLoading(false);
      return;
    }

    let unsubscribe: (() => void) | null = null;

    // Delay license loading to not block initial render
    const timer = setTimeout(() => {
      unsubscribe = onSnapshot(
        doc(firestore, 'users', user.uid),
        (doc) => {
          const data = doc.data();
          if (data?.license) {
            setLicense({
              type: data.license.type,
              validUntil: data.license.validUntil?.toDate(),
              features: LICENSE_FEATURES[data.license.type as LicenseType],
            });
          } else {
            // Default to standard if no license set
            setLicense({
              type: 'unpickle_standard',
              features: LICENSE_FEATURES.unpickle_standard,
            });
          }
          setLoading(false);
        },
        (error) => {
          console.error('Error fetching license:', error);
          // Default to standard on error
          setLicense({
            type: 'unpickle_standard',
            features: LICENSE_FEATURES.unpickle_standard,
          });
          setLoading(false);
        }
      );
    }, 200); // Delay license check to prioritize page render

    return () => {
      clearTimeout(timer);
      if (unsubscribe) unsubscribe();
    };
  }, [user, firestore]);

  const hasFeature = (feature: Feature): boolean => {
    if (!license) return false;
    return license.features.includes(feature);
  };

  const getLimit = (limitType: keyof typeof LICENSE_LIMITS[LicenseType]): number | null => {
    if (!license) return 0;
    return LICENSE_LIMITS[license.type][limitType];
  };

  const canAddProject = (currentOwnedCount: number): boolean => {
    // Only owned projects count toward the limit, shared projects are free
    const limit = getLimit('maxProjects');
    return limit === null || currentOwnedCount < limit;
  };

  const canAddTask = (currentCount: number): boolean => {
    const limit = getLimit('maxTasksPerProject');
    return limit === null || currentCount < limit;
  };

  const canAddContributor = (currentContributorCount: number): boolean => {
    // This count should NOT include the owner - only additional contributors
    const limit = getLimit('maxContributors');
    return limit === null || currentContributorCount < limit;
  };

  const isFeatureUpgradable = (feature: Feature): boolean => {
    return !hasFeature(feature) && LICENSE_FEATURES.unpickle_pro.includes(feature);
  };

  return (
    <LicenseContext.Provider
      value={{
        license,
        loading,
        hasFeature,
        canAddProject,
        canAddTask,
        canAddContributor,
        getLimit,
        isFeatureUpgradable,
      }}
    >
      {children}
    </LicenseContext.Provider>
  );
}

export function useLicense() {
  const context = useContext(LicenseContext);
  if (context === undefined) {
    throw new Error('useLicense must be used within a LicenseProvider');
  }
  return context;
}
